/**
 * client/hud/wager.ts — placement, "in the money" and wager payouts (pure).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * Sources [research/sticks-and-stones.md §6]:
 *  - Placement (_globallogic.gsc:1840-1849): pointstowin descending, ties -> fewer deaths.
 *  - In the money (_wager.gsc:538-571): place <= 3, tied players share a place.
 *  - Payout (_wager.gsc:187-239 calculatefreeforallpayouts): percentages 0.5/0.3/0.2, with 2
 *    players 0.7/0.3, with 1 player 1.0; players with equal score form a group that splits the
 *    sum of their places' percentages: int(pot * sum / groupSize). (BO2 groups by pers["score"];
 *    we only have pointstowin, so we group by it - noted quirk.)
 *  - Pot: scr_wagerBet x players. BO2 Party Games have no CoD Points; the remake shows a notional
 *    10 CP ante (BO1's lowest wager tier) - ESTIMATE / display only.
 */
import type { ScoreRow } from '@shared/types';

export const WAGER_DISPLAY_ANTE = 10;

/** Rows sorted by placement (does not mutate the input). */
export function sortPlacement(rows: readonly ScoreRow[]): ScoreRow[] {
  return [...rows].sort((a, b) => b.points - a.points || a.deaths - b.deaths || a.clientNum - b.clientNum);
}

/** Place (1-based) per clientNum; players with equal points AND deaths share a place. */
export function placesOf(sorted: readonly ScoreRow[]): Map<number, number> {
  const out = new Map<number, number>();
  for (let i = 0; i < sorted.length; i++) {
    const r = sorted[i]!, prev = sorted[i - 1];
    const place = prev && prev.points === r.points && prev.deaths === r.deaths ? out.get(prev.clientNum)! : i + 1;
    out.set(r.clientNum, place);
  }
  return out;
}

/** _wager.gsc: currentscoreplace <= 3 (places shared by equal score). */
export function isInTheMoney(sorted: readonly ScoreRow[], clientNum: number): boolean {
  const me = sorted.find((r) => r.clientNum === clientNum);
  if (!me) return false;
  // place by score only (ties share), like _wager.gsc updates from .score
  let better = 0;
  for (const r of sorted) if (r.points > me.points) better++;
  return better + 1 <= 3;
}

/** calculatefreeforallpayouts(): winnings per clientNum for a placement-sorted list. */
export function wagerPayouts(sorted: readonly ScoreRow[], pot: number): Map<number, number> {
  const out = new Map<number, number>();
  for (const r of sorted) out.set(r.clientNum, 0);
  const n = sorted.length;
  if (n <= 1) return out; // a single player: escrow returned, no payout (_wager.gsc level.players.size == 1)
  const pct = n === 2 ? [0.7, 0.3] : [0.5, 0.3, 0.2];
  let group: ScoreRow[] = [sorted[0]!];
  let cum = pct[0]!;
  let idx = 0;
  const flush = () => { for (const r of group) out.set(r.clientNum, Math.trunc((pot * cum) / group.length)); };
  for (let i = 1; i < n; i++) {
    const r = sorted[i]!;
    if (r.points < group[0]!.points) {
      flush();
      group = [];
      cum = 0;
    }
    group.push(r);
    idx++;
    if (pct[idx] !== undefined) cum += pct[idx]!;
  }
  flush();
  return out;
}

/** The best score among players other than `clientNum` (FFA "enemyScore"), 0 if alone. */
export function bestOtherScore(rows: readonly ScoreRow[], clientNum: number): number {
  let best = 0;
  let any = false;
  for (const r of rows) if (r.clientNum !== clientNum) { best = any ? Math.max(best, r.points) : r.points; any = true; }
  return best;
}

/** istopscoringplayer (_globallogic.gsc:1903-1946): top pointstowin and not 0. */
export function topScorers(rows: readonly ScoreRow[]): Set<number> {
  let max = 0;
  for (const r of rows) max = Math.max(max, r.points);
  const s = new Set<number>();
  if (max <= 0) return s;
  for (const r of rows) if (r.points === max) s.add(r.clientNum);
  return s;
}
